import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
  SizeColumnsToFitGridStrategy,
  GridReadyEvent,
  GetRowIdParams,
} from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import GridTableLoading from './GridTableLoading';

export interface IGridTable<RowDataType> {
  gridRef?: React.RefObject<AgGridReact<RowDataType>>;
  height?: number | string;
  rowData: RowDataType[]; // rowData에 T 타입을 사용하여 유연성 확보
  columnDefs: ColDef<RowDataType>[]; // columnDefs에 T 타입을 적용
  onCellValueChanged?: (params: any) => void; // Cell 값 변경 시 이벤트
  autoSizeStrategy?: boolean; // 셀 value 크기에 맞춰서 업데이트하게 (아마 주문 등록 엑셀 업로드에서만 사용)
  suppressCellFocus?: boolean; // 셀 포커스 기능
  onRowDoubleClicked?: (params: any) => void; // Row 더블클릭 이벤트
  rowSelection?: any;
  isLoading?: boolean;
  rowClassRules?: {
    [cssClass: string]: (
      params: import('ag-grid-community').RowClassParams
    ) => boolean;
  };
  onSelectionChanged?: (params: any) => void; // Selection 변경 시 이벤트
  isRowSelectable?: (params: any) => boolean; // Row 선택 가능 여부 판단 함수
  getRowId?: (params: GetRowIdParams<RowDataType>) => string; // Row ID 제공해 selection 유지
}

/**
 * @author taulmax
 * @date 2024.10.15
 * @description AG GRID TABLE 컴포넌트
 */
const GridTable = <RowDataType,>({
  gridRef,
  height,
  rowData,
  columnDefs,
  onCellValueChanged,
  autoSizeStrategy,
  suppressCellFocus = true,
  onRowDoubleClicked,
  rowSelection,
  isLoading,
  rowClassRules,
  onSelectionChanged,
  isRowSelectable,
  getRowId,
}: IGridTable<RowDataType>) => {
  const fitCellContents:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: 'fitCellContents',
  };

  const onGridReady: any = useCallback((params: GridReadyEvent): void => {
    // 테이블 너비보다 컬럼이 길 경우 가로 스크롤을 만들기 위해 주석 처리
    // params.api.sizeColumnsToFit();
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      minWidth: 100,
      resizable: true,
    };
  }, []);

  return (
    <div
      className="ag-theme-quartz" // applying the Data Grid theme
      style={{ height, position: 'relative' }} // the Data Grid will fill the size of the parent container
    >
      {isLoading && <GridTableLoading />}
      <AgGridReact
        ref={gridRef}
        onGridReady={onGridReady}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={getRowId}
        autoSizeStrategy={autoSizeStrategy ? fitCellContents : undefined}
        alwaysShowVerticalScroll={true}
        tooltipShowDelay={0} // 툴팁 딜레이 설정 (필요에 따라 조정)
        tooltipHideDelay={2000} // 툴팁 숨김 딜레이 설정
        onCellValueChanged={onCellValueChanged}
        suppressCellFocus={suppressCellFocus}
        onRowDoubleClicked={onRowDoubleClicked}
        rowSelection={rowSelection}
        rowClassRules={rowClassRules}
        onSelectionChanged={onSelectionChanged}
        isRowSelectable={isRowSelectable}
      />
    </div>
  );
};

export default GridTable;
